import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet } from "@/lib/apiClient";
import { CrmAccountsListClient, type CrmAccountsListRow } from "@/components/crm/CrmAccountsListClient";
import { CrmEmptyState } from "@/components/crm/CrmEmptyState";

export function CrmAccountsPage() {
  const [searchParams] = useSearchParams();
  const initialQuery = (searchParams.get("q") || "").trim();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<CrmAccountsListRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const res = await apiGet<{ accounts?: CrmAccountsListRow[] }>("/api/crm/accounts?fetchAll=true");
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error || "Could not load accounts.");
        setAccounts([]);
      } else {
        setError(null);
        const rows = (res.data?.accounts ?? []).filter(
          (a) => a.id && !String(a.id).startsWith("lead_") && Boolean(a.name?.trim()),
        );
        setAccounts(rows);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const namedCount = useMemo(() => accounts.length, [accounts]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Accounts</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Company and person accounts for your pipeline.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/crm/import?target=accounts"
            className="rounded-md border border-indigo-300 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-900 hover:bg-indigo-100 dark:border-indigo-500/40 dark:bg-indigo-950/40 dark:text-indigo-100"
          >
            Import
          </Link>
          <Link
            to="/crm/accounts/new"
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
          >
            New company account
          </Link>
          <Link
            to="/crm/accounts/new/individual"
            className="rounded-md border border-indigo-300 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-900 hover:bg-indigo-100 dark:border-indigo-500/40 dark:bg-indigo-950/40 dark:text-indigo-100"
          >
            New person account
          </Link>
        </div>
      </div>

      {loading ? <p className="text-sm text-slate-500">Loading accounts…</p> : null}
      {error ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-200">
          CRM accounts are unavailable. {error}
        </div>
      ) : null}

      {!loading && !error && namedCount === 0 ? (
        <CrmEmptyState
          title="No accounts yet"
          description="Accounts hold company or person records for your pipeline. Create one directly, import a CSV, or convert a lead when it is ready."
          primaryCta={{ href: "/crm/accounts/new", label: "Create your first account" }}
          secondaryCta={{ href: "/crm/import?target=accounts", label: "Import CSV" }}
        />
      ) : null}

      {!loading && !error && namedCount > 0 ? (
        <CrmAccountsListClient accounts={accounts} initialQuery={initialQuery} />
      ) : null}
    </div>
  );
}
